/**
 * Conversation History API Service
 * 
 * Handles all API calls related to conversation history, customer profiles,
 * and enhanced conversation management with comprehensive tracking data.
 * 
 * Features:
 * - Complete conversation history with message timeline
 * - Rich customer profiles with device, marketing, and behavioral data
 * - Real-time conversation list management
 * - Agent assignment and handoff functionality
 */

import { api } from './api'
import * as Types from '../types'
import { logger } from '../utils/logger'

// Type aliases to fix module resolution issues
type ConversationHistory = Types.ConversationHistory
type CustomerProfile = Types.CustomerProfile
type ConversationAssignment = Types.ConversationAssignment
type Agent = Types.Agent

export interface ConversationListResponse {
  conversations: ConversationAssignment[]
  total_count: number
  pending_count: number
  assigned_count: number
}

export interface ConversationHistoryService {
  // Conversation list management
  getConversationList(): Promise<ConversationListResponse>
  
  // Detailed conversation data
  getConversationHistory(conversationId: string): Promise<ConversationHistory>
  
  // Agent assignment
  assignConversation(conversationId: string, agentId: string, reason: string, context?: string): Promise<void>
  getAvailableAgents(): Promise<Agent[]>
}

/**
 * Implementation of conversation history service with comprehensive error handling
 * and detailed logging for debugging and monitoring.
 */
class ConversationHistoryServiceImpl implements ConversationHistoryService {
  
  /**
   * Fetch all conversations requiring attention with basic info for list view
   * Both agents and company owners use the same endpoint to see all conversations.
   * Agents can see all conversations so they can assign conversations to themselves.
   * 
   * @returns Promise<ConversationListResponse> List of conversations with counts
   */
  async getConversationList(): Promise<ConversationListResponse> {
    try {
      const userType = localStorage.getItem('user_type')
      
      // Both agents and company owners use the same endpoint
      const endpoint = '/conversations/assignments'
      logger.info(`🔍 Fetching conversation assignments for ${userType}`)
      
      const { data } = await api.get<ConversationListResponse>(endpoint)
      
      logger.info(`✅ Retrieved ${data.total_count} conversations (${data.pending_count} pending) for ${userType}`)
      
      return data
      
    } catch (error) {
      logger.error('❌ Error fetching conversation list:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        errorObject: error,
        endpoint: '/conversations/assignments'
      })
      throw new Error('Failed to load conversations. Please try again.')
    }
  }
  
  /**
   * Get complete conversation history with customer profile and message timeline
   * 
   * @param conversationId Unique conversation identifier
   * @returns Promise<ConversationHistory> Complete conversation data
   */
  async getConversationHistory(conversationId: string): Promise<ConversationHistory> {
    try {
      logger.info(`📖 Fetching conversation history: ${conversationId}`)
      
      if (!conversationId) {
        throw new Error('Conversation ID is required')
      }
      
      const response = await api.get(`/conversations/${conversationId}/history`)
      
      logger.debug('🔍 Raw API response:', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: Object.keys(response.data || {}),
        success: response.data?.success,
        hasNestedData: !!response.data?.data
      })
      
      // Try to extract history data with fallback
      let history: ConversationHistory
      if (response.data.data) {
        history = response.data.data as ConversationHistory
      } else {
        // Fallback - maybe API returns data directly
        history = response.data as ConversationHistory
        logger.warn('⚠️ Using fallback data extraction method')
      }
      
      logger.info(`✅ Retrieved conversation history: ${history?.total_messages || 0} messages`)
      
      // Log customer profile summary for debugging
      const profile = history.customer_profile
      if (profile) {
        logger.debug('👤 Customer profile summary:', {
          device: `${profile.browser_name || 'Unknown'} • ${profile.operating_system || 'Unknown'}`,
          location: `${profile.country || 'Unknown'}, ${profile.city || 'Unknown'}`,
          marketing: `${profile.utm_source || 'Direct'} • ${profile.utm_medium || 'None'}`,
          session: `${profile.session_duration || 0}s • ${profile.pages_visited || 0} pages`
        })
      }
      
      return history
      
    } catch (error) {
      logger.error(`❌ Error fetching conversation history for ${conversationId}:`, error)
      throw new Error('Failed to load conversation details. Please try again.')
    }
  }
  
  
  /**
   * Assign conversation to a specific agent with context
   * 
   * @param conversationId Conversation to assign
   * @param agentId Agent to assign conversation to
   * @param reason Reason for assignment
   * @param context Optional additional context
   */
  async assignConversation(
    conversationId: string, 
    agentId: string, 
    reason: string, 
    context?: string
  ): Promise<void> {
    try {
      logger.info(`👨‍💼 Assigning conversation ${conversationId} to agent ${agentId}`)
      
      if (!conversationId || !agentId || !reason) {
        throw new Error('Conversation ID, agent ID, and reason are required')
      }
      
      const requestData = {
        to_agent_id: agentId,
        handoff_reason: reason,
        context_summary: context
      }
      
      const response = await api.post(`/conversations/${conversationId}/assign`, requestData)
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to assign conversation')
      }
      
      logger.info(`✅ Successfully assigned conversation ${conversationId} to agent ${agentId}`)
      
    } catch (error) {
      logger.error(`❌ Error assigning conversation ${conversationId}:`, error)
      throw new Error('Failed to assign conversation. Please try again.')
    }
  }
  
  /**
   * Get list of available agents for assignment
   * 
   * @returns Promise<Agent[]> List of active agents
   */
  async getAvailableAgents(): Promise<Agent[]> {
    try {
      logger.info('👥 Fetching available agents for assignment')
      
      const response = await api.get<Agent[]>('/agents/')
      
      // The API returns the data directly, not wrapped in an ApiResponse object
      const agents = response.data
      const availableAgents = agents.filter(agent => 
        agent.is_active && agent.current_chat_count < agent.max_concurrent_chats
      )
      
      logger.info(`✅ Retrieved ${availableAgents.length} available agents (${agents.length} total)`)
      
      return availableAgents
      
    } catch (error) {
      logger.error('❌ Error fetching available agents:', error)
      throw new Error('Failed to load agents. Please try again.')
    }
  }
  
  /**
   * Get list of conversation assignments for queue management
   * 
   * @param filters Optional filters for status and priority
   * @returns Promise<ConversationAssignment[]> List of conversation assignments
   */
  async getConversationAssignments(filters?: { status?: string; priority?: string }): Promise<ConversationAssignment[]> {
    try {
      logger.info('📋 Fetching conversation assignments', { filters })
      
      // Use the correct endpoint directly instead of calling getConversationList
      const response = await api.get<ConversationListResponse>('/conversations/assignments')
      
      logger.debug('🔍 API Response structure:', {
        hasData: !!response.data,
        dataKeys: Object.keys(response.data || {}),
        dataType: typeof response.data,
        rawData: response.data
      })
      
      // Check if response.data has conversations property
      const conversations = response.data?.conversations || response.data || []
      
      // Apply client-side filters if needed
      let assignments = Array.isArray(conversations) ? conversations : []
      
      if (filters?.status && filters.status !== 'all') {
        assignments = assignments.filter(conv => conv.status === filters.status)
      }
      
      if (filters?.priority && filters.priority !== 'all') {
        assignments = assignments.filter(conv => conv.priority === filters.priority)
      }
      
      logger.info(`✅ Retrieved ${assignments.length} conversation assignments`, { 
        statusFilter: filters?.status || 'all',
        priorityFilter: filters?.priority || 'none',
        totalBeforeFilter: conversations.length || 0
      })
      
      return assignments
      
    } catch (error) {
      logger.error('❌ Error fetching conversation assignments:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        errorObject: error
      })
      throw new Error('Failed to load conversation assignments. Please try again.')
    }
  }
  
}

// Export singleton instance
export const conversationHistoryService = new ConversationHistoryServiceImpl()

// Export service interface for dependency injection
export { ConversationHistoryServiceImpl }

/**
 * Utility functions for conversation data manipulation
 */
export const ConversationUtils = {
  
  /**
   * Format device info for display
   * @param profile Customer profile
   * @returns Formatted device string like "Chrome 120 • Windows 11"
   */
  formatDeviceInfo(profile: CustomerProfile): string {
    const browser = profile.browser_name ? 
      `${profile.browser_name}${profile.browser_version ? ' ' + profile.browser_version.split('.')[0] : ''}` : 
      'Unknown Browser'
    
    const os = profile.operating_system || 'Unknown OS'
    
    return `${browser} • ${os}`
  },
  
  /**
   * Format location info for display
   * @param profile Customer profile
   * @returns Formatted location string like "New York, US • English"
   */
  formatLocationInfo(profile: CustomerProfile): string {
    const location = profile.city && profile.country ? 
      `${profile.city}, ${profile.country}` : 
      profile.country || 'Unknown Location'
    
    const language = profile.language ? 
      new Intl.DisplayNames(['en'], { type: 'language' }).of(profile.language.split('-')[0]) :
      null
    
    return language ? `${location} • ${language}` : location
  },
  
  /**
   * Format marketing attribution for display
   * @param profile Customer profile
   * @returns Formatted attribution string like "Google Organic • /pricing"
   */
  formatMarketingInfo(profile: CustomerProfile): string {
    const source = profile.utm_source || 'Direct'
    const medium = profile.utm_medium ? ` ${profile.utm_medium}` : ''
    const page = profile.page_url ? 
      new URL(profile.page_url).pathname : 
      null
    
    return page ? `${source}${medium} • ${page}` : `${source}${medium}`
  },
  
  /**
   * Calculate conversation duration from first to last message
   * @param messages Array of messages
   * @returns Duration in seconds
   */
  calculateConversationDuration(messages: ConversationHistory['messages']): number {
    if (messages.length < 2) return 0
    
    const first = new Date(messages[0].timestamp).getTime()
    const last = new Date(messages[messages.length - 1].timestamp).getTime()
    
    return Math.round((last - first) / 1000)
  },
  
  /**
   * Format wait time for display
   * @param seconds Wait time in seconds
   * @returns Human-readable format like "4m 30s"
   */
  formatWaitTime(seconds: number): string {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  }
}